import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'

import {
  ColorVariant,
  CornerRadius,
  getShapeClass,
  MaterialIcon,
  parseSpacing,
  RawSpacingInput,
  spacingToStyles,
} from '@makyoapp/makyo-tw-ui'

import { IDBTable } from '@/core/db'
import { naturalSort } from '@/core/db/operations/sorting'
import { IRow } from '@/core/models/tables/row'
import { useCompBackgroundColor, useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { UserColumnFilterType, UserColumnFilterValue } from '@/shared/builder/types/user-filter'
import { MAX_ROWS_LIMIT } from '@/shared/constants'

import { CompOverlayDevmode } from '../CompOverlayDevmode'
import { FilterChip } from './FilterChip'
import { FilterDrawer } from './FilterDrawer'
import { FilterFields } from './FilterFields'

function getTypeDescription(type: UserColumnFilterType) {
  switch (type) {
    case UserColumnFilterType.NumberExactAmount:
      return 'Type an exact amount'
    case UserColumnFilterType.NumberRange:
      return 'Pick a range'
    case UserColumnFilterType.DateDateRange:
      return 'Select a date range'
    case UserColumnFilterType.DateExactDate:
      return 'Choose a specific date'
    case UserColumnFilterType.RatingAmountWithin:
      return 'Choose a rating value'
    case UserColumnFilterType.RatingAtLeast:
      return 'Minimum'
    case UserColumnFilterType.NumberSelection:
    case UserColumnFilterType.TextSelection:
    case UserColumnFilterType.TextCheckbox:
    case UserColumnFilterType.TextSegmented:
      return 'Select an option'
    default:
      return null
  }
}

interface CompFilterV2Props {
  /** component id */
  id?: string
  value: Record<string, unknown>
  setValue: Dispatch<SetStateAction<Record<string, unknown>>>
  settings: UserColumnFilterValue[]
  table: IDBTable
  filterChipColor?: ColorVariant
  filterOverlayBgColor?: ColorVariant
  filterCornerRadius?: CornerRadius
  filterMargin?: RawSpacingInput
  inline?: boolean
}

/**
 * New CompFilterV2 Component
 *
 */
export function CompFilterV2({
  id,
  // setter
  value = {},
  setValue = () => {},
  // data
  settings = [],
  table,
  // style
  filterChipColor = 'primary-95',
  filterOverlayBgColor = 'primary-95',
  filterCornerRadius = 'medium',
  filterMargin = { top: 0, bottom: 0, left: 0, right: 0 },
  inline = false,
}: CompFilterV2Props) {
  const { language, userSpecific } = useDbClientContext()
  const { currentParentBg } = useCompBackgroundColor()
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { component } = useCompContext()

  const isSameChipBg = currentParentBg === filterChipColor

  //  ================================ STATE ================================
  const [allRows, setAllRows] = useState<IRow[]>([])
  const [showFilterView, setShowFilterView] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  // get all rows from current table
  useEffect(() => {
    if (!table) return

    const getData = async () => {
      const data = await table.GetFormattedData(language, userSpecific, {
        page: 0,
        perPage: MAX_ROWS_LIMIT,
      })
      setAllRows(data.rows)
    }

    getData()

    const unsubscribe = table.subscribe(getData)

    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleShowFilter = (index: number) => {
    setShowFilterView(true)
    setActiveIndex(index)
  }

  const handleCloseFilter = useCallback(() => {
    setShowFilterView(false)
    setActiveIndex(-1)
  }, [])

  // normal (immediate) change
  const handleChange = useCallback(
    (...args: Parameters<typeof setValue>) => {
      setValue(...args)
    },
    [setValue]
  )

  const handleSubmit = useCallback(
    (values: any) => {
      setValue(values)
      handleCloseFilter()
    },
    [setValue, handleCloseFilter]
  )

  // initialize an optimized columns options map for each filter setting,
  // used to avoid re-calculating the options for each filter
  const columnOptions = useMemo(() => {
    const optionsMap = new Map<string, any[]>()

    const uniqueItemsByColumn = new Map<string, Map<any, { id: string; label: string; value: any }>>()
    settings.forEach(({ column }) => {
      uniqueItemsByColumn.set(column, new Map())
    })

    for (const row of allRows) {
      for (const columnID of uniqueItemsByColumn.keys()) {
        const item = row[columnID]
        if (item?.value != null) {
          const uniqueItems = uniqueItemsByColumn.get(columnID)!
          if (!!item.value && !uniqueItems.has(String(item.value).toLowerCase())) {
            uniqueItems.set(String(item.value).toLowerCase(), {
              id: row._id as string,
              label: item.toString(),
              value: item.value,
            })
          }
        }
      }
    }

    for (const [columnID, uniqueItems] of uniqueItemsByColumn.entries()) {
      const uniqRows = Array.from(uniqueItems.values())
      const sortedRows = naturalSort(uniqRows).asc((u) => u.value)
      optionsMap.set(columnID, sortedRows)
    }

    return optionsMap
    //
  }, [allRows, settings])

  // fast uniqBy method alternative
  const getRowOptions = useCallback(
    (columnID: string) => {
      return columnOptions.get(columnID) || []
    },
    [columnOptions]
  )

  //  ================================ ELEMENTS ================================

  // render filter overlay title
  const filterOverlayTitle = useMemo(() => {
    // all filters
    if (activeIndex === -1) {
      return (
        <div className={clsx('flex items-center gap-3 px-2', `text-on-${filterOverlayBgColor}`)}>
          <MaterialIcon icon='tune' size={28} />
          <span className='text-lg font-semibold'>Filters</span>
        </div>
      )
    }

    // single filter
    const setting = settings[activeIndex]
    const colName = table?.columnsMap.get(setting.column)?._Name
    const sheetDesc = getTypeDescription(setting.filterType)
    return (
      <div className={clsx('flex items-center gap-3 px-2', `text-on-${filterOverlayBgColor}`)}>
        <MaterialIcon icon={setting.icon || 'tune'} size={30} />
        <div>
          <div className='text-lg font-semibold'>{setting.label || colName || ''}</div>
          {sheetDesc && <div className='text-sm opacity-60'>({sheetDesc})</div>}
        </div>
      </div>
    )
  }, [activeIndex, filterOverlayBgColor, settings, table?.columnsMap])

  //  ================================ RENDER ================================

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // skip if no settings or table
  if (!settings.length || !table) return null

  const render = (
    <div
      className='flex items-center'
      style={{
        ...spacingToStyles(parseSpacing(filterMargin), 'margin'),
      }}
    >
      <div className={clsx('flex items-center gap-2.5 no-scrollbar overflow-y-hidden overflow-x-scroll')}>
        {/* Modal trigger button */}
        <button
          onClick={() => setShowFilterView(true)}
          className={clsx(
            'p-2 min-h-10 min-w-10 flex items-center justify-center cursor-pointer outline-none ring-0 focus:ring-0 focus:outline-none',
            isSameChipBg && `border border-on-${filterChipColor}/50`,
            getShapeClass(filterCornerRadius),
            `bg-${filterChipColor} text-on-${filterChipColor} hover:bg-${filterChipColor}/90`
          )}
        >
          <MaterialIcon icon='tune' size={18} className={`text-on-${filterChipColor}`} />
        </button>

        {/* Filter Chips */}
        {!inline && (
          <div className='flex gap-2.5 max-h-28 justify-center items-center'>
            {settings
              .filter((setting) => setting.showOnFilter)
              .map((setting, idx) => (
                <FilterChip
                  key={`${setting.column}-${idx}`}
                  setting={setting}
                  column={table.columnsMap.get(setting.column)!}
                  rowOptions={getRowOptions(setting.column)}
                  value={value[setting.column]}
                  onChange={(value) => {
                    handleChange((prev) => {
                      let filter = { ...prev }
                      if (typeof value === 'undefined' || (Array.isArray(value) && value.length === 0)) {
                        delete filter[setting.column]
                      } else {
                        filter[setting.column] = value
                      }
                      return filter
                    })
                  }}
                  onShowFilter={() => handleShowFilter(idx)}
                  chipColor={filterChipColor}
                  cornerRadius={filterCornerRadius}
                />
              ))}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <FilterDrawer
        open={showFilterView}
        onClose={handleCloseFilter}
        title={filterOverlayTitle}
        bgColor={filterOverlayBgColor}
      >
        <FilterFields
          value={value}
          onSubmit={handleSubmit}
          activeIndex={activeIndex}
          settings={settings}
          table={table}
          bgColor={filterOverlayBgColor}
          getTypeDescription={getTypeDescription}
          getRowOptions={getRowOptions}
        />
      </FilterDrawer>
    </div>
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {render}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return render
}
